import { useState } from 'react'

import { t } from '../../../../../../../common/i18n.js'
import type { Language } from '../../../../../../../common/types/i18n.js'
import { formatDisplayDate } from '../../../../../../../common/utils/format-utils.js'
import { Button } from '../../../../../../components/button/button.js'
import { CalendarCheck, CalendarX, Scale } from '../../../../../../components/icon/solar-icons.js'
import { Section, SectionTitle } from '../../../../../../components/section/section.js'
import type { LeaveSettlement } from '../../employee-profile-types.js'

interface TimeAwaySettlementCardProps {
    settlement: LeaveSettlement
    lang: Language
}

// Read-only TLS §69/§71 final-settlement preview for admins: what payroll
// would pay out (earned but unused days, §71) or withhold (upfront days used
// before being earned, §69) if employment ended on the given date. Nothing is
// written to the ledger — payroll acts on the figure outside the portal.
// The figures stay collapsed behind a button by default — they're sensitive
// and only occasionally needed, so the card opens on demand.
export const TimeAwaySettlementCard = ({ settlement, lang }: TimeAwaySettlementCardProps) => {
    const view = t.portal.timeOff.view
    const [expanded, setExpanded] = useState(false)

    const verdict =
        settlement.settlementDays > 0
            ? view.settlementPayout(settlement.settlementDays)(lang)
            : settlement.settlementDays < 0
              ? view.settlementWithhold(Math.abs(settlement.settlementDays))(lang)
              : view.settlementEven(lang)

    return (
        <Section className="time-away__details-card">
            <div className="time-away__settlement-header">
                <SectionTitle>{view.settlementTitle(lang)}</SectionTitle>
                <Button
                    size="small"
                    variant="secondary"
                    text={expanded ? view.settlementHide(lang) : view.settlementShow(lang)}
                    onClick={() => setExpanded((prev) => !prev)}
                />
            </div>
            {expanded && (
                <>
                    <div className="time-away__detail-note">
                        {view.settlementSubtitle(
                            formatDisplayDate(settlement.terminationDate, 'long', lang),
                        )(lang)}
                    </div>
                    <div className="time-away__detail-row">
                        <span className="time-away__detail-icon">
                            <CalendarCheck size={15} />
                        </span>
                        <span className="time-away__detail-label">
                            {view.settlementEarned(lang)}
                        </span>
                        <span className="time-away__detail-value">
                            {view.dayCount(settlement.earnedToDate)(lang)}
                        </span>
                    </div>
                    <div className="time-away__detail-row">
                        <span className="time-away__detail-icon">
                            <CalendarX size={15} />
                        </span>
                        <span className="time-away__detail-label">
                            {view.settlementUnearned(lang)}
                        </span>
                        <span className="time-away__detail-value">
                            {view.dayCount(settlement.unearnedGranted)(lang)}
                        </span>
                    </div>
                    <div className="time-away__detail-row">
                        <span className="time-away__detail-icon">
                            <Scale size={15} />
                        </span>
                        <span className="time-away__detail-label">
                            {view.settlementBalance(lang)}
                        </span>
                        <span className="time-away__detail-value">{verdict}</span>
                    </div>
                </>
            )}
        </Section>
    )
}
